import React, { useEffect, useState, useRef, useMemo } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { API_URL } from "..";
import Switch from "react-switch";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  GoogleMap,
  Marker,
  Circle,
  useLoadScript,
} from "@react-google-maps/api";
import Swal from "sweetalert2";

function formatDateTimeJob(dateInput) {
  if (!dateInput) return "N/a";

  const date = new Date(dateInput);

  const options = {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  };

  let formatted = date
    .toLocaleDateString("en-GB", options)
    .replace(",", "")
    .replace(/(\d{2}:\d{2}) (\w{2})/, "$1 $2");

  formatted = formatted
    .replace(/\bjan\b/i, "Jan")
    .replace(/\bfeb\b/i, "Feb")
    .replace(/\bmar\b/i, "Mar")
    .replace(/\bapr\b/i, "Apr")
    .replace(/\bmay\b/i, "May")
    .replace(/\bjun\b/i, "Jun")
    .replace(/\bjul\b/i, "Jul")
    .replace(/\baug\b/i, "Aug")
    .replace(/\bsep\b/i, "Sep")
    .replace(/\boct\b/i, "Oct")
    .replace(/\bnov\b/i, "Nov")
    .replace(/\bdec\b/i, "Dec");

  return formatted.replace(/ (\d{2}:\d{2}) /, " $1 ");
}

function formatDate(inputDate) {
  if (!inputDate) return "N/a";

  const date = new Date(inputDate);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const day = date.getUTCDate();
  const month = months[date.getUTCMonth()];
  const year = date.getUTCFullYear();

  return `${day} ${month} ${year}`;
}

function formatDateTime(isoString) {
  if (!isoString) return "N/a";

  const date = new Date(isoString);

  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${day} ${month} ${year} ${hours}:${minutes} ${ampm}`;
}

const MAPBOX_ACCESS_TOKEN =
  "pk.eyJ1IjoiemFtcCIsImEiOiJjbTkxa3dkcnMwMmQxMnJzYzBobW9hOHBiIn0.GWvvqPrWqtMby0HYCYdjMQ";

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 1,
};

const getJobStatusLabel = (row) => {
  if (row?.isClosed === true) return "closed";

  if (
    row?.status === "accepted" ||
    row?.status === "In Progress" ||
    row?.status === "active" ||
    row?.status === "Arrived"
  ) {
    return "active";
  }

  return row?.status || "N/A";
};

const getRowClass = (row) => {
  if (row?.isClosed === true) return "custom-job-row dt-row-closed";

  if (row?.status === "completed") {
    return "custom-job-row dt-row-completed";
  }

  if (
    row?.status === "accepted" ||
    row?.status === "In Progress" ||
    row?.status === "active" ||
    row?.status === "Arrived"
  ) {
    return "custom-job-row dt-row-active";
  }

  if (row?.status === "new") {
    return "custom-job-row dt-row-new";
  }

  if (row?.status === "declined") {
    return "custom-job-row dt-row-declined";
  }

  return "custom-job-row dt-row-new";
};

const getMobileCardClass = (row) => {
  if (row?.isClosed === true) return "custom-mobile-job-card dt-row-closed";

  if (row?.status === "completed") {
    return "custom-mobile-job-card dt-row-completed";
  }

  if (
    row?.status === "accepted" ||
    row?.status === "In Progress" ||
    row?.status === "active" ||
    row?.status === "Arrived"
  ) {
    return "custom-mobile-job-card dt-row-active";
  }

  if (row?.status === "new") {
    return "custom-mobile-job-card dt-row-new";
  }

  if (row?.status === "declined") {
    return "custom-mobile-job-card dt-row-declined";
  }

  return "custom-mobile-job-card dt-row-new";
};

const getStatusBadgeClass = (row) => {
  if (row?.isClosed === true) return "badge bg-success";

  if (row?.status === "new") return "badge bg-primary";

  if (
    row?.status === "accepted" ||
    row?.status === "In Progress" ||
    row?.status === "active" ||
    row?.status === "Arrived"
  ) {
    return "badge bg-warning";
  }

  if (row?.status === "declined") return "badge bg-danger";

  return "badge bg-success";
};

const getSortValue = (row, key) => {
  switch (key) {
    case "id":
      return Number(row?.id) || 0;

    case "client_name":
      return row?.client_name || "";

    case "phone":
      return row?.phone || "";

    case "completionDate":
      return row?.completionDate ? new Date(row.completionDate).getTime() : 0;

    case "assignedTo":
      return row?.assignedTo?.name || "";

    case "job_date_set":
      return row?.job_date_set ? new Date(row.job_date_set).getTime() : 0;

    case "createdAt":
      return row?.createdAt ? new Date(row.createdAt).getTime() : 0;

    case "status":
      return getJobStatusLabel(row);

    default:
      return "";
  }
};

const AppointmentLayer = ({
  data,
  pagination = DEFAULT_PAGINATION,
  onPageChange,
  onLimitChange,
  loading,
  searchTerm = "",
  searchColumn = "all",
  onSearchTermChange,
  onSearchColumnChange,
  onSearchSubmit,
  onSearchClear,
}) => {
  const history = useNavigate();

const searchColumnOptions = [
  { value: "all", label: "All Columns" },
  { value: "id", label: "Job ID" },
  { value: "referenceNo", label: "Reference No" },
  { value: "client_name", label: "Client Name" },
  { value: "phone", label: "Client Phone" },
  { value: "email", label: "Client Email" },
  { value: "address", label: "Address" },
  { value: "postcode", label: "Postcode" },
  { value: "service_required", label: "Service Required" },
  { value: "status", label: "Status" },
];

const hasSearchValue =
  String(searchTerm || "").trim() !== "" || searchColumn !== "all";

const handleSearchKeyDown = (event) => {
  if (event.key === "Enter" && onSearchSubmit) {
    onSearchSubmit();
  }
};
  const [jobDateSet, setJobDateSet] = useState(
    new Date().toISOString().slice(0, 16)
  );

  const [electricianList, setElectricianList] = useState([]);
  const [workforceList, setWorkforceList] = useState([]);

  const [selectedElectricians, setSelectedElectricians] = useState([]);
  const [selectedWorkForce, setSelectedWorkForce] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showCustomerInfo, setShowCustomerInfo] = useState(true);
  const [isJobLocationCustomerAddress, setIsJobLocationCustomerAddress] =
    useState(true);

  const [jobLocation, setJobLocation] = useState("");
  const [jobPostcode, setJobPostcode] = useState("");
  const [jobLocationCoordinates, setJobLocationCoordinates] = useState(null);

  const [referenceNo, setreferenceNo] = useState("");
  const [jobLocationSuggestions, setJobLocationSuggestions] = useState([]);

  const [enableGeofencing, setEnableGeofencing] = useState(false);
  const [showModalForClosed, setShowModalForClosed] = useState(false);
  const [radius, setRadius] = useState(100);

  const [mapCenter, setMapCenter] = useState({
    lat: 51.5074,
    lng: -0.1276,
  });

  const [markerPosition, setMarkerPosition] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "desc",
  });

  const jobLocationInputRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyBtQlR_uF7q-S8TQblCzLw7Fy7BvyIeXAg",
    libraries: ["places"],
  });

  const mapContainerStyle = {
    width: "100%",
    height: "300px",
    borderRadius: "8px",
    border: "1px solid #ced4da",
  };

  const fetchElectrician = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/fetch-electrician`, {
        method: "GET",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      if (response.status === 200) {
        setElectricianList(
          result.data.map((e) => ({
            label: e.name,
            value: e.id,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching electricians:", error);
    }
  };

  const fetchWorkforce = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/workforce/select-workforce`,
        {
          method: "GET",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      if (response.status === 200) {
        setWorkforceList(
          result.data.map((e) => ({
            label: e.fullName,
            value: e.id,
          }))
        );
      } else {
        throw new Error(result.message || "Failed to fetch workforce");
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  const fetchJobDetails = async (jobId) => {
    try {
      const response = await fetch(`${API_URL}/api/job/fetch-single/${jobId}`, {
        method: "GET",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      if (response.status === 200) {
        const job = result.job;

        setShowCustomerInfo(job.showCustomerInfo || true);

        setIsJobLocationCustomerAddress(
          !job.address || job.address === job.customer?.address
        );

        setJobLocation(job.address || "");
        setJobPostcode(job.postcode || "");
        setEnableGeofencing(job.enableGeofencing || false);
        setRadius(job.radius || 100);

        if (job.map_location) {
          const [latitude, longitude] = job.map_location
            .split(",")
            .map(Number);

          setJobLocationCoordinates({
            latitude,
            longitude,
          });

          setMapCenter({
            lat: latitude,
            lng: longitude,
          });

          setMarkerPosition({
            lat: latitude,
            lng: longitude,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
    }
  };

  const fetchCoordinates = async (address) => {
    if (!address) {
      return {
        latitude: "",
        longitude: "",
      };
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          address
        )}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=GB`
      );

      const result = await response.json();

      if (result.features?.length > 0) {
        const [longitude, latitude] = result.features[0].geometry.coordinates;

        return {
          latitude,
          longitude,
        };
      }

      return {
        latitude: "",
        longitude: "",
      };
    } catch (error) {
      console.error("Error fetching coordinates:", error);

      return {
        latitude: "",
        longitude: "",
      };
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete job?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const apiCall = await fetch(`${API_URL}/api/job/delete-single/${id}`, {
        method: "DELETE",
      });

      if (apiCall.status === 200) {
        window.location.reload();
      }
    } catch (error) {
      console.error("Error deleting job:", error);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedElectricians.length) {
      newErrors.electricians = "At least one electrician is required";
    }

    if (!isJobLocationCustomerAddress) {
      if (!jobLocation) newErrors.jobLocation = "Job location is required";
      if (!jobPostcode) newErrors.jobPostcode = "Job postcode is required";
    }

    if (referenceNo === "") {
      newErrors.referenceNo = "Reference No is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const closeAssignModal = () => {
    setShowModal(false);
    setSelectedElectricians([]);
    setSelectedWorkForce([]);
    setSelectedJobId(null);
    setJobLocation("");
    setJobPostcode("");
    setJobLocationCoordinates(null);
    setEnableGeofencing(false);
    setRadius(100);
    setErrors({});
    setreferenceNo("");
  };

  const handleAssignElectrician = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      let mapLocation = "";

      if (!isJobLocationCustomerAddress && jobLocation) {
        const coords = await fetchCoordinates(jobLocation);

        mapLocation =
          coords.latitude && coords.longitude
            ? `${coords.latitude},${coords.longitude}`
            : "";
      } else {
        const job = data.find((j) => j.id === selectedJobId);

        if (job?.map_location) {
          mapLocation = job.map_location;
        }
      }

      const response = await fetch(
        `${API_URL}/api/job/assign-electrician/${selectedJobId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            referenceNo: referenceNo,
            posted_ids: selectedElectricians.map((e) => e.value).toString(),
            workforce_ids: selectedWorkForce.map((e) => e.value).toString(),
            showCustomerInfo: showCustomerInfo ? 1 : 0,
            address: isJobLocationCustomerAddress ? null : jobLocation,
            postcode: isJobLocationCustomerAddress ? null : jobPostcode,
            map_location: mapLocation,
            enableGeofencing: enableGeofencing ? 1 : 0,
            radius: enableGeofencing ? radius : null,
            job_date_set: jobDateSet,
          }),
        }
      );

      if (response.status === 200) {
        closeAssignModal();
        window.location.reload();
      } else {
        throw new Error("Failed to assign electricians");
      }
    } catch (error) {
      console.error("Error assigning electricians:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openModalForClose = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This job will be marked as closed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, sure",
      cancelButtonText: "Not now",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const api = await fetch(`${API_URL}/api/job/mark-as-closed`, {
          method: "POST",
          body: JSON.stringify({
            id: id,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
          },
        });

        if (api.status === 200) {
          window.location.reload();
        }
      }
    });
  };

  const openAssignModal = (row) => {
    setSelectedJobId(row.id);
    fetchJobDetails(row.id);
    setShowModal(true);
  };

  useEffect(() => {
    fetchElectrician();
    fetchWorkforce();
  }, []);

  useEffect(() => {
    const fetchSuggestions = async (query) => {
      if (!query) {
        setJobLocationSuggestions([]);
        return;
      }

      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            query
          )}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=GB`
        );

        const result = await response.json();

        const suggestions = result.features.map((feature) => ({
          label: feature.place_name,
          value: feature.place_name,
          coordinates: feature.geometry.coordinates,
        }));

        setJobLocationSuggestions(suggestions);
      } catch (error) {
        console.error("Error fetching job location suggestions:", error);
      }
    };

    const handleInput = (e) => {
      const value = e.target.value;

      setJobLocation(value);
      setJobLocationCoordinates(null);
      fetchSuggestions(value);
    };

    const jobInput = jobLocationInputRef.current;

    if (jobInput && !isJobLocationCustomerAddress) {
      jobInput.addEventListener("input", handleInput);
    }

    return () => {
      if (jobInput) {
        jobInput.removeEventListener("input", handleInput);
      }
    };
  }, [isJobLocationCustomerAddress]);

  useEffect(() => {
    if (isJobLocationCustomerAddress) {
      const job = data.find((j) => j.id === selectedJobId);

      if (job?.map_location) {
        const [latitude, longitude] = job.map_location.split(",").map(Number);

        setMarkerPosition({
          lat: latitude,
          lng: longitude,
        });

        setMapCenter({
          lat: latitude,
          lng: longitude,
        });
      }
    } else if (jobLocationCoordinates) {
      setMarkerPosition({
        lat: jobLocationCoordinates.latitude,
        lng: jobLocationCoordinates.longitude,
      });

      setMapCenter({
        lat: jobLocationCoordinates.latitude,
        lng: jobLocationCoordinates.longitude,
      });
    }
  }, [
    isJobLocationCustomerAddress,
    jobLocationCoordinates,
    selectedJobId,
    data,
  ]);

  const electricianOptions = useMemo(() => {
    return electricianList.filter((opt) => {
      const selectedInWorkforce = selectedWorkForce.some(
        (w) => w.value === opt.value
      );

      const selectedInElectrician = selectedElectricians.some(
        (e) => e.value === opt.value
      );

      if (selectedInElectrician) return true;

      return !selectedInWorkforce;
    });
  }, [electricianList, selectedElectricians, selectedWorkForce]);

  const workforceOptions = useMemo(() => {
    return workforceList.filter((opt) => {
      const selectedInElectrician = selectedElectricians.some(
        (e) => e.value === opt.value
      );

      const selectedInWorkforce = selectedWorkForce.some(
        (w) => w.value === opt.value
      );

      if (selectedInWorkforce) return true;

      return !selectedInElectrician;
    });
  }, [workforceList, selectedElectricians, selectedWorkForce]);

  const handleJobLocationSelect = (selected) => {
    if (selected) {
      setJobLocation(selected.value);

      setJobLocationCoordinates({
        latitude: selected.coordinates[1],
        longitude: selected.coordinates[0],
      });

      setJobLocationSuggestions([]);

      setErrors((prev) => ({
        ...prev,
        jobLocation: null,
      }));
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key,
        direction: "asc",
      };
    });
  };

  const sortedData = useMemo(() => {
    const rows = Array.isArray(data) ? [...data] : [];

    rows.sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

    return rows;
  }, [data, sortConfig]);

  const { page = 1, limit = 25, total = 0, totalPages = 1 } = pagination || {};

  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(page * limit, total);

  const handlePrev = () => {
    if (page > 1 && onPageChange) onPageChange(page - 1);
  };

  const handleNext = () => {
    if (page < totalPages && onPageChange) onPageChange(page + 1);
  };

  const handleFirst = () => {
    if (page > 1 && onPageChange) onPageChange(1);
  };

  const handleLast = () => {
    if (page < totalPages && onPageChange) onPageChange(totalPages);
  };

  const handleLimitSelect = (e) => {
    const newLimit = Number(e.target.value);

    if (onLimitChange) {
      onLimitChange(newLimit);
    }
  };

  const pageNumbers = useMemo(() => {
    const pages = [];
    const windowSize = 1;

    const addPage = (p) => pages.push(p);
    const addEllipsis = () => pages.push("...");

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) addPage(i);
      return pages;
    }

    addPage(1);

    if (page - windowSize > 2) {
      addEllipsis();
    }

    const start = Math.max(2, page - windowSize);
    const end = Math.min(totalPages - 1, page + windowSize);

    for (let i = start; i <= end; i++) {
      addPage(i);
    }

    if (page + windowSize < totalPages - 1) {
      addEllipsis();
    }

    addPage(totalPages);

    return pages;
  }, [page, totalPages]);

  const SortIcon = ({ sortKey }) => {
    if (sortConfig.key !== sortKey) {
      return <Icon icon="solar:sort-vertical-linear" className="sort-icon" />;
    }

    return sortConfig.direction === "asc" ? (
      <Icon icon="solar:alt-arrow-up-linear" className="sort-icon active" />
    ) : (
      <Icon icon="solar:alt-arrow-down-linear" className="sort-icon active" />
    );
  };
const ActionTooltipButton = ({
  tooltip,
  className,
  icon,
  onClick,
  disabled = false,
}) => {
  return (
    <OverlayTrigger
      placement="top"
      delay={{ show: 180, hide: 80 }}
      overlay={
        <Tooltip id={`tooltip-${tooltip.replace(/\s+/g, "-").toLowerCase()}`}>
          {tooltip}
        </Tooltip>
      }
    >
      <span className="stable-action-tooltip-wrap">
        <button
          type="button"
          className={`stable-action-btn ${className}`}
          aria-label={tooltip}
          title={tooltip}
          onClick={onClick}
          disabled={disabled}
        >
          <Icon icon={icon} />
        </button>
      </span>
    </OverlayTrigger>
  );
};
  const renderActions = (row) => {
  return (
    <div className="stable-action-bar">
      <ActionTooltipButton
        tooltip="View Job"
        className="view"
        icon="ph:eye"
        onClick={() => history(`/jobs/view/${row.id}`)}
      />

      <ActionTooltipButton
        tooltip="Edit Job"
        className="edit"
        icon="ph:pencil-simple-line"
        onClick={() => history(`/jobs/edit/${row.id}`)}
      />

      {row?.job?.length === 0 && (
        <ActionTooltipButton
          tooltip="Create Invoice"
          className="invoice"
          icon="ph:file-plus"
          onClick={() => history(`/jobs/invoices/${row.id}`)}
        />
      )}

      {row?.job?.length !== 0 && (
        <ActionTooltipButton
          tooltip="View Invoice"
          className="invoice"
          icon="ph:file-text"
          onClick={() => history(`/jobs/view/invoices/${row.id}`)}
        />
      )}

      {row?.isClosed === false && (
        <ActionTooltipButton
          tooltip="Mark as Closed"
          className="close-job"
          icon="ph:check-circle"
          onClick={() => openModalForClose(row.id)}
        />
      )}

      {row?.status === "new" && row?.job_posted_to === null && (
        <ActionTooltipButton
          tooltip="Assign Electrician"
          className="assign"
          icon="ph:user-plus"
          onClick={() => openAssignModal(row)}
        />
      )}

      <ActionTooltipButton
        tooltip="Delete Job"
        className="delete"
        icon="ph:trash"
        onClick={() => handleDelete(row.id)}
      />
    </div>
  );
};

  return (
    <div className="card basic-data-table custom-jobs-table-card">
      <style>
        {`
        .job-search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  width: min(100%, 920px);
  flex-wrap: wrap;
}

.job-search-column {
  min-height: 42px;
  width: 220px;
  border-radius: 10px;
  border: 1px solid #dbe4f0;
  font-weight: 700;
  color: #334155;
}

.job-search-input {
  min-height: 42px;
  flex: 1;
  min-width: 260px;
}

.job-clear-btn {
  min-height: 42px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0 16px;
  background: #ffffff;
  color: #475569;
  font-weight: 700;
  cursor: pointer;
}

.job-clear-btn:hover:not(:disabled) {
  background: #f8fafc;
  color: #0f172a;
}

.job-search-btn {
  min-height: 42px;
  border: 0;
  border-radius: 10px;
  padding: 0 16px;
  background: #2563eb;
  color: #ffffff;
  font-weight: 800;
  cursor: pointer;
}

.job-search-btn:disabled,
.job-clear-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
          .custom-jobs-table-card {
            border: 1px solid #e8eef7;
            border-radius: 18px;
            overflow: hidden;
            box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
          }

          .custom-jobs-table-card .card-header {
            border-bottom: 1px solid #edf2f7;
            background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
            padding: 18px 20px;
          }

          .custom-jobs-table-card .card-title {
            font-size: 18px;
            font-weight: 800;
            color: #0f172a;
          }

          .custom-table-toolbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            flex-wrap: wrap;
            margin-bottom: 14px;
          }

          .custom-table-hint {
            font-size: 13px;
            color: #64748b;
          }

          .custom-table-scroll {
            width: 100%;
            overflow-x: auto;
            border: 1px solid #e8eef7;
            border-radius: 14px;
          }

          .custom-jobs-table {
            width: 100%;
            min-width: 1180px;
            margin: 0;
            border-collapse: separate;
            border-spacing: 0;
          }

          .custom-jobs-table thead th {
            position: sticky;
            top: 0;
            z-index: 2;
            background: #f8fafc;
            color: #334155;
            font-size: 12px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            border-bottom: 1px solid #e2e8f0;
            padding: 13px 14px;
            white-space: nowrap;
          }

          .sortable-th {
            cursor: pointer;
            user-select: none;
          }

          .th-content {
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }

          .sort-icon {
            font-size: 15px;
            color: #94a3b8;
          }

          .sort-icon.active {
            color: #2563eb;
          }

          .custom-jobs-table tbody td {
            padding: 13px 14px;
            border-bottom: 1px solid rgba(226, 232, 240, 0.8);
            vertical-align: middle;
            font-size: 14px;
            color: #334155;
          }

          .custom-jobs-table tbody tr:last-child td {
            border-bottom: none;
          }

          .custom-job-row {
            position: relative;
            transition: transform 0.15s ease, box-shadow 0.15s ease;
          }

          .custom-job-row:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
          }

          .custom-jobs-table tbody tr.dt-row-new,
          .custom-mobile-job-card.dt-row-new {
            border-left: 4px solid #0d6efd;
          }

          .custom-jobs-table tbody tr.dt-row-active,
          .custom-mobile-job-card.dt-row-active {
            border-left: 4px solid #ffc107;
          }

          .custom-jobs-table tbody tr.dt-row-completed,
          .custom-mobile-job-card.dt-row-completed {
            border-left: 4px solid #198754;
          }

          .custom-jobs-table tbody tr.dt-row-closed,
          .custom-mobile-job-card.dt-row-closed {
            border-left: 4px solid #6c757d;
          }

          .custom-jobs-table tbody tr.dt-row-declined,
          .custom-mobile-job-card.dt-row-declined {
            border-left: 4px solid #dc3545;
          }

          .custom-jobs-table tbody tr.dt-row-completed td,
          .custom-jobs-table tbody tr.dt-row-completed td span,
          .custom-jobs-table tbody tr.dt-row-completed td button,
          .custom-jobs-table tbody tr.dt-row-closed td,
          .custom-jobs-table tbody tr.dt-row-closed td span,
          .custom-jobs-table tbody tr.dt-row-closed td button {
            color: #ffffff !important;
          }

          .custom-jobs-table tbody tr.dt-row-completed .job-id-link,
          .custom-jobs-table tbody tr.dt-row-closed .job-id-link {
            color: #ffffff !important;
            text-decoration-color: rgba(255, 255, 255, 0.9);
          }
            .stable-action-tooltip-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}

.stable-action-tooltip-wrap .stable-action-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

.tooltip {
  font-size: 12px;
  font-weight: 600;
}

.tooltip-inner {
  border-radius: 8px;
  padding: 6px 10px;
}

          .custom-jobs-table tbody tr.dt-row-completed .client-name-cell,
          .custom-jobs-table tbody tr.dt-row-closed .client-name-cell,
          .custom-jobs-table tbody tr.dt-row-completed .muted-cell,
          .custom-jobs-table tbody tr.dt-row-closed .muted-cell {
            color: #ffffff !important;
          }

          .custom-mobile-job-card.dt-row-completed,
          .custom-mobile-job-card.dt-row-closed {
            color: #ffffff;
          }

          .custom-mobile-job-card.dt-row-completed span,
          .custom-mobile-job-card.dt-row-completed div,
          .custom-mobile-job-card.dt-row-completed button,
          .custom-mobile-job-card.dt-row-closed span,
          .custom-mobile-job-card.dt-row-closed div,
          .custom-mobile-job-card.dt-row-closed button {
            color: #ffffff !important;
          }

          .custom-mobile-job-card.dt-row-completed .mobile-label,
          .custom-mobile-job-card.dt-row-closed .mobile-label {
            color: rgba(255, 255, 255, 0.75) !important;
          }

          .job-id-link {
            border: 0;
            background: transparent;
            padding: 0;
            color: #2563eb;
            font-weight: 800;
            text-decoration: underline;
            cursor: pointer;
          }

          .client-name-cell {
            display: inline-block;
            max-width: 220px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-weight: 700;
            color: #0f172a;
          }

          .muted-cell {
            color: #64748b;
          }

          .stable-action-bar {
            display: inline-flex;
            align-items: center;
            justify-content: flex-end;
            gap: 6px;
            min-width: 190px;
            white-space: nowrap;
          }

          .stable-action-btn {
            width: 32px;
            height: 32px;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            background: #ffffff;
            color: #334155 !important;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 17px;
            cursor: pointer;
            transition: all 0.15s ease;
            box-shadow: 0 4px 10px rgba(15, 23, 42, 0.06);
            flex-shrink: 0;
          }

          .stable-action-btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 16px rgba(15, 23, 42, 0.12);
          }

          .stable-action-btn.view {
            background: #eff6ff;
            border-color: #bfdbfe;
            color: #2563eb !important;
          }

          .stable-action-btn.edit {
            background: #f5f3ff;
            border-color: #ddd6fe;
            color: #7c3aed !important;
          }

          .stable-action-btn.invoice {
            background: #ecfdf5;
            border-color: #bbf7d0;
            color: #059669 !important;
          }

          .stable-action-btn.close-job {
            background: #f8fafc;
            border-color: #cbd5e1;
            color: #475569 !important;
          }

          .stable-action-btn.assign {
            background: #fff7ed;
            border-color: #fed7aa;
            color: #ea580c !important;
          }

          .stable-action-btn.delete {
            background: #fef2f2;
            border-color: #fecaca;
            color: #dc2626 !important;
          }

          .custom-jobs-table tbody tr.dt-row-completed .stable-action-btn,
          .custom-jobs-table tbody tr.dt-row-closed .stable-action-btn,
          .custom-mobile-job-card.dt-row-completed .stable-action-btn,
          .custom-mobile-job-card.dt-row-closed .stable-action-btn {
            background: rgba(255, 255, 255, 0.95);
            border-color: rgba(255, 255, 255, 0.75);
          }

          .custom-jobs-table tbody tr.dt-row-completed .stable-action-btn.view,
          .custom-jobs-table tbody tr.dt-row-closed .stable-action-btn.view,
          .custom-mobile-job-card.dt-row-completed .stable-action-btn.view,
          .custom-mobile-job-card.dt-row-closed .stable-action-btn.view {
            color: #2563eb !important;
          }

          .custom-jobs-table tbody tr.dt-row-completed .stable-action-btn.edit,
          .custom-jobs-table tbody tr.dt-row-closed .stable-action-btn.edit,
          .custom-mobile-job-card.dt-row-completed .stable-action-btn.edit,
          .custom-mobile-job-card.dt-row-closed .stable-action-btn.edit {
            color: #7c3aed !important;
          }

          .custom-jobs-table tbody tr.dt-row-completed .stable-action-btn.invoice,
          .custom-jobs-table tbody tr.dt-row-closed .stable-action-btn.invoice,
          .custom-mobile-job-card.dt-row-completed .stable-action-btn.invoice,
          .custom-mobile-job-card.dt-row-closed .stable-action-btn.invoice {
            color: #059669 !important;
          }

          .custom-jobs-table tbody tr.dt-row-completed .stable-action-btn.close-job,
          .custom-jobs-table tbody tr.dt-row-closed .stable-action-btn.close-job,
          .custom-mobile-job-card.dt-row-completed .stable-action-btn.close-job,
          .custom-mobile-job-card.dt-row-closed .stable-action-btn.close-job {
            color: #475569 !important;
          }

          .custom-jobs-table tbody tr.dt-row-completed .stable-action-btn.assign,
          .custom-jobs-table tbody tr.dt-row-closed .stable-action-btn.assign,
          .custom-mobile-job-card.dt-row-completed .stable-action-btn.assign,
          .custom-mobile-job-card.dt-row-closed .stable-action-btn.assign {
            color: #ea580c !important;
          }

          .custom-jobs-table tbody tr.dt-row-completed .stable-action-btn.delete,
          .custom-jobs-table tbody tr.dt-row-closed .stable-action-btn.delete,
          .custom-mobile-job-card.dt-row-completed .stable-action-btn.delete,
          .custom-mobile-job-card.dt-row-closed .stable-action-btn.delete {
            color: #dc2626 !important;
          }

          .custom-mobile-job-list {
            display: none;
          }

          .custom-mobile-job-card {
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            padding: 14px;
            margin-bottom: 12px;
            background: #ffffff;
          }

          .mobile-card-top {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            align-items: flex-start;
            margin-bottom: 10px;
          }

          .mobile-client-name {
            font-weight: 800;
            color: #0f172a;
            font-size: 15px;
            margin-top: 4px;
          }

          .mobile-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px;
            margin-top: 12px;
          }

          .mobile-label {
            display: block;
            font-size: 11px;
            color: #64748b;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.03em;
            margin-bottom: 3px;
          }

          .mobile-value {
            font-size: 13px;
            color: #334155;
            word-break: break-word;
          }

          .mobile-card-actions {
            margin-top: 12px;
            display: flex;
            justify-content: flex-end;
            overflow-x: auto;
            padding-bottom: 2px;
          }

          .mobile-card-actions .stable-action-bar {
            min-width: auto;
            flex-wrap: wrap;
            justify-content: flex-end;
          }

          .custom-pagination-footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 12px;
            padding: 16px 0 0;
            border-top: 1px solid #e9ecef;
            margin-top: 16px;
          }

          .custom-pagination-footer .rows-per-page {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .custom-pagination-footer select {
            width: auto;
            padding: 4px 28px 4px 10px;
          }

          .custom-pagination-footer .pagination-info {
            color: #6c757d;
            font-size: 0.85rem;
            white-space: nowrap;
          }

          .custom-pagination-footer .pagination-nav {
            display: flex;
            align-items: center;
            gap: 4px;
            flex-wrap: wrap;
          }

          .custom-pagination-footer .page-btn {
            min-width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid #dee2e6;
            background: #fff;
            border-radius: 6px;
            font-size: 0.85rem;
            cursor: pointer;
            color: #343a40;
            padding: 0 8px;
          }

          .custom-pagination-footer .page-btn:hover:not(:disabled) {
            background: #f1f3f5;
          }

          .custom-pagination-footer .page-btn:disabled {
            opacity: 0.45;
            cursor: not-allowed;
          }

          .custom-pagination-footer .page-btn.active {
            background: #007bff;
            border-color: #007bff;
            color: #fff;
            font-weight: 600;
          }

          .custom-pagination-footer .page-ellipsis {
            padding: 0 4px;
            color: #adb5bd;
          }

          .form-label {
            font-weight: 600;
            color: #343a40;
          }

          .form-control,
          .form-select {
            border-radius: 8px;
            padding: 0.75rem;
            transition: all 0.2s ease;
          }

          .form-control:focus,
          .form-select:focus {
            border-color: #80bdff;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          }

          .modal-content {
            border-radius: 12px;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
          }

          .address-suggestions {
            position: absolute;
            z-index: 1000;
            background: white;
            border: 1px solid #ced4da;
            border-radius: 8px;
            width: 100%;
            max-height: 200px;
            overflow-y: auto;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            margin-top: 0.25rem;
            padding: 0;
            list-style: none;
          }

          .address-suggestions li {
            padding: 0.75rem;
            cursor: pointer;
            transition: background-color 0.2s ease;
          }

          .address-suggestions li:hover {
            background-color: #f8f9fa;
          }

          .invalid-feedback {
            display: block;
          }

          @media (max-width: 920px) {
            .custom-table-scroll {
              display: none;
            }

            .custom-mobile-job-list {
              display: block;
            }

            .custom-pagination-footer {
              align-items: stretch;
              flex-direction: column;
            }

            .custom-pagination-footer .pagination-nav {
              justify-content: center;
            }
          }

          @media (max-width: 640px) {
            .custom-jobs-table-card .card-header,
            .custom-jobs-table-card .card-body {
              padding-left: 12px;
              padding-right: 12px;
            }

            .mobile-grid {
              grid-template-columns: 1fr;
            }

            .custom-pagination-footer .rows-per-page {
              justify-content: space-between;
            }

            .custom-pagination-footer .pagination-info {
              white-space: normal;
              text-align: center;
            }

            .stable-action-bar {
              gap: 5px;
              min-width: auto;
            }

            .stable-action-btn {
              width: 31px;
              height: 31px;
              font-size: 16px;
              border-radius: 9px;
            }
          }
        `}
      </style>

    <div className="card-header">
  <div className="custom-table-toolbar mb-0">
    <div>
      <h5 className="card-title mb-1">List of Jobs</h5>
      <div className="custom-table-hint">
       
      </div>
    </div>

    {loading && (
      <span className="badge bg-primary">
        <span className="spinner-border spinner-border-sm me-1" />
        Loading
      </span>
    )}
  </div>

  <div className="job-search-box mt-3">
    <select
      className="form-select job-search-column"
      value={searchColumn}
      onChange={(e) =>
        onSearchColumnChange && onSearchColumnChange(e.target.value)
      }
      disabled={loading}
    >
      {searchColumnOptions.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>

    <input
      type="text"
      className="form-control job-search-input"
      placeholder={
        searchColumn === "all"
          ? "Search by client, phone, email, address"
          : `Search by ${
              searchColumnOptions.find((item) => item.value === searchColumn)
                ?.label || "selected column"
            }...`
      }
      value={searchTerm}
      onChange={(e) =>
        onSearchTermChange && onSearchTermChange(e.target.value)
      }
      onKeyDown={handleSearchKeyDown}
      disabled={loading}
    />

    <button
      type="button"
      className="job-clear-btn"
      onClick={onSearchClear}
      disabled={loading || !hasSearchValue}
    >
      Clear
    </button>

    <button
      type="button"
      className="job-search-btn"
      onClick={onSearchSubmit}
      disabled={loading}
    >
      Search
    </button>
  </div>
</div>

      <div className="card-body">
     <div className="custom-table-toolbar"></div>

        <div className="custom-table-scroll">
          <table className="custom-jobs-table">
            <thead>
              <tr>
                <th className="sortable-th" onClick={() => handleSort("id")}>
                  <span className="th-content">
                    S.L <SortIcon sortKey="id" />
                  </span>
                </th>

                <th
                  className="sortable-th"
                  onClick={() => handleSort("client_name")}
                >
                  <span className="th-content">
                    Client Name <SortIcon sortKey="client_name" />
                  </span>
                </th>

                <th className="sortable-th" onClick={() => handleSort("phone")}>
                  <span className="th-content">
                    Client Phone <SortIcon sortKey="phone" />
                  </span>
                </th>

                <th
                  className="sortable-th"
                  onClick={() => handleSort("completionDate")}
                >
                  <span className="th-content">
                    Completion Date <SortIcon sortKey="completionDate" />
                  </span>
                </th>

                <th
                  className="sortable-th"
                  onClick={() => handleSort("assignedTo")}
                >
                  <span className="th-content">
                    Accepted By <SortIcon sortKey="assignedTo" />
                  </span>
                </th>

                <th
                  className="sortable-th"
                  onClick={() => handleSort("job_date_set")}
                >
                  <span className="th-content">
                    Job Date <SortIcon sortKey="job_date_set" />
                  </span>
                </th>

                <th
                  className="sortable-th"
                  onClick={() => handleSort("createdAt")}
                >
                  <span className="th-content">
                    Created Date <SortIcon sortKey="createdAt" />
                  </span>
                </th>

                <th
                  className="sortable-th"
                  onClick={() => handleSort("status")}
                >
                  <span className="th-content">
                    Status <SortIcon sortKey="status" />
                  </span>
                </th>

                <th>Action</th>
              </tr>
            </thead>

           <tbody>
  {sortedData.length === 0 ? (
    <tr>
      <td colSpan={9} className="text-center py-4 text-muted">
        No job data found
      </td>
    </tr>
  ) : (
    sortedData.map((row) => (
      <tr key={row.id} className={getRowClass(row)}>
        <td>
          <button
            type="button"
            className="job-id-link"
            title={String(row.id)}
            onClick={() => history(`/jobs/view/${row.id}`)}
          >
            {row.id}
          </button>
        </td>

        <td>
          <span className="client-name-cell" title={row.client_name}>
            {row.client_name || "N/A"}
          </span>
        </td>

        <td>
          <span className="muted-cell">{row.phone || "N/A"}</span>
        </td>

        <td>
          <span className="muted-cell">
            {row.completionDate ? formatDateTime(row.completionDate) : "N/A"}
          </span>
        </td>

        <td>
          <span className="muted-cell">
            {row?.assignedTo?.name || "N/A"}
          </span>
        </td>

        <td>
          <span className="muted-cell">
            {row.job_date_set ? formatDateTimeJob(row.job_date_set) : "N/A"}
          </span>
        </td>

        <td>
          <span className="muted-cell">
            {row.createdAt ? formatDate(row.createdAt) : "N/A"}
          </span>
        </td>

        <td>
          <span className={getStatusBadgeClass(row)}>
            {getJobStatusLabel(row)}
          </span>
        </td>

        <td>{renderActions(row)}</td>
      </tr>
    ))
  )}
</tbody>
          </table>
        </div>

        <div className="custom-mobile-job-list">
  {sortedData.length === 0 ? (
    <div className="custom-mobile-job-card text-center text-muted">
      No job data found
    </div>
  ) : (
    sortedData.map((row) => (
      <div key={row.id} className={getMobileCardClass(row)}>
        <div className="mobile-card-top">
          <div>
            <button
              type="button"
              className="job-id-link"
              onClick={() => history(`/jobs/view/${row.id}`)}
            >
              #{row.id}
            </button>

            <div className="mobile-client-name">
              {row.client_name || "N/A"}
            </div>
          </div>

          <span className={getStatusBadgeClass(row)}>
            {getJobStatusLabel(row)}
          </span>
        </div>

        <div className="mobile-grid">
          <div>
            <span className="mobile-label">Phone</span>
            <span className="mobile-value">{row.phone || "N/A"}</span>
          </div>

          <div>
            <span className="mobile-label">Accepted By</span>
            <span className="mobile-value">
              {row?.assignedTo?.name || "N/A"}
            </span>
          </div>

          <div>
            <span className="mobile-label">Completion</span>
            <span className="mobile-value">
              {row.completionDate
                ? formatDateTime(row.completionDate)
                : "N/A"}
            </span>
          </div>

          <div>
            <span className="mobile-label">Job Date</span>
            <span className="mobile-value">
              {row.job_date_set
                ? formatDateTimeJob(row.job_date_set)
                : "N/A"}
            </span>
          </div>

          <div>
            <span className="mobile-label">Created</span>
            <span className="mobile-value">
              {row.createdAt ? formatDate(row.createdAt) : "N/A"}
            </span>
          </div>
        </div>

        <div className="mobile-card-actions">{renderActions(row)}</div>
      </div>
    ))
  )}
</div>

        <div className="custom-pagination-footer">
          <div className="rows-per-page">
            <span className="pagination-info">Rows per page:</span>

            <select
              className="form-select form-select-sm"
              value={limit}
              onChange={handleLimitSelect}
              disabled={loading}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <span className="pagination-info">
            {total === 0
              ? "No entries"
              : `Showing ${rangeStart}-${rangeEnd} of ${total}`}
          </span>

          <div className="pagination-nav">
            <button
              type="button"
              className="page-btn"
              onClick={handleFirst}
              disabled={page <= 1 || loading}
              title="First page"
            >
              <Icon icon="bi:chevron-double-left" />
            </button>

            <button
              type="button"
              className="page-btn"
              onClick={handlePrev}
              disabled={page <= 1 || loading}
              title="Previous page"
            >
              <Icon icon="bi:chevron-left" />
            </button>

            {pageNumbers.map((p, idx) =>
              p === "..." ? (
                <span key={`ellipsis-${idx}`} className="page-ellipsis">
                  ...
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  className={`page-btn ${p === page ? "active" : ""}`}
                  onClick={() => onPageChange && onPageChange(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              )
            )}

            <button
              type="button"
              className="page-btn"
              onClick={handleNext}
              disabled={page >= totalPages || loading}
              title="Next page"
            >
              <Icon icon="bi:chevron-right" />
            </button>

            <button
              type="button"
              className="page-btn"
              onClick={handleLast}
              disabled={page >= totalPages || loading}
              title="Last page"
            >
              <Icon icon="bi:chevron-double-right" />
            </button>
          </div>
        </div>
      </div>

      {showModalForClosed && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmation</h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModalForClosed(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Assign Electricians</h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={closeAssignModal}
                />
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="referenceNo" className="form-label">
                    Reference No <span className="text-danger">*</span>
                  </label>

                  <input
                    type="text"
                    className={`form-control ${
                      errors.referenceNo ? "is-invalid" : ""
                    }`}
                    id="referenceNo"
                    name="referenceNo"
                    placeholder="Enter Reference No"
                    value={referenceNo}
                    onChange={(e) => setreferenceNo(e.target.value)}
                  />

                  {errors.referenceNo && (
                    <div className="invalid-feedback">{errors.referenceNo}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="electricianSelect" className="form-label">
                    Select Electricians <span className="text-danger">*</span>
                  </label>

                  <Select
                    id="electricianSelect"
                    options={electricianOptions}
                    value={selectedElectricians}
                    onChange={(value) => setSelectedElectricians(value || [])}
                    placeholder="Select Electricians"
                    isMulti
                  />

                  {errors.electricians && (
                    <div className="invalid-feedback">{errors.electricians}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="workforceSelect" className="form-label">
                    Select Workforce
                  </label>

                  <Select
                    id="workforceSelect"
                    options={workforceOptions}
                    value={selectedWorkForce}
                    onChange={(value) => setSelectedWorkForce(value || [])}
                    placeholder="Select Workforce"
                    isMulti
                  />
                </div>

                <div className="col-md-12 mb-3">
                  <label htmlFor="jobDateSet" className="form-label">
                    Job Date <span className="text-danger">*</span>
                  </label>

                  <input
                    type="datetime-local"
                    id="jobDateSet"
                    name="jobDateSet"
                    className="form-control"
                    value={jobDateSet}
                    onChange={(e) => setJobDateSet(e.target.value)}
                  />

                  <div className="form-text">
                    Select the date and time for this job.
                  </div>
                </div>

                <div className="mb-3">
                  <div className="form-check form-switch">
                    <Switch
                      className="form-check-input"
                      id="showCustomerInfoSwitch"
                      checked={showCustomerInfo}
                      onChange={(checked) => setShowCustomerInfo(checked)}
                    />

                    <label
                      className="form-check-label"
                      htmlFor="showCustomerInfoSwitch"
                    >
                      &nbsp; &nbsp;Show Customer Information to Electrician
                    </label>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="form-check form-switch">
                    <Switch
                      className="form-check-input"
                      id="jobLocationSwitch"
                      checked={isJobLocationCustomerAddress}
                      onChange={(checked) => {
                        setIsJobLocationCustomerAddress(checked);

                        if (checked) {
                          setJobLocation("");
                          setJobPostcode("");
                          setJobLocationCoordinates(null);
                          setJobLocationSuggestions([]);

                          setErrors((prev) => ({
                            ...prev,
                            jobLocation: null,
                            jobPostcode: null,
                          }));
                        }
                      }}
                    />

                    <label
                      className="form-check-label"
                      htmlFor="jobLocationSwitch"
                    >
                      &nbsp; &nbsp;Job Location is Customer Address
                    </label>
                  </div>
                </div>

                {!isJobLocationCustomerAddress && (
                  <div className="row">
                    <div className="col-md-8 mb-3">
                      <label htmlFor="jobLocation" className="form-label">
                        Job Location <span className="text-danger">*</span>
                      </label>

                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          className={`form-control ${
                            errors.jobLocation ? "is-invalid" : ""
                          }`}
                          id="jobLocation"
                          name="jobLocation"
                          placeholder="Enter job location"
                          value={jobLocation}
                          onChange={(e) => setJobLocation(e.target.value)}
                          ref={jobLocationInputRef}
                        />

                        {errors.jobLocation && (
                          <div className="invalid-feedback">
                            {errors.jobLocation}
                          </div>
                        )}

                        {jobLocationSuggestions.length > 0 && (
                          <ul className="address-suggestions">
                            {jobLocationSuggestions.map((suggestion, index) => (
                              <li
                                key={index}
                                onClick={() =>
                                  handleJobLocationSelect(suggestion)
                                }
                              >
                                {suggestion.label}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label htmlFor="jobPostcode" className="form-label">
                        Job Postcode <span className="text-danger">*</span>
                      </label>

                      <input
                        type="text"
                        className={`form-control ${
                          errors.jobPostcode ? "is-invalid" : ""
                        }`}
                        id="jobPostcode"
                        name="jobPostcode"
                        placeholder="Enter postcode"
                        value={jobPostcode}
                        onChange={(e) => setJobPostcode(e.target.value)}
                      />

                      {errors.jobPostcode && (
                        <div className="invalid-feedback">
                          {errors.jobPostcode}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <div className="form-check form-switch">
                    <Switch
                      className="form-check-input"
                      id="geofencingSwitch"
                      checked={enableGeofencing}
                      onChange={(checked) => setEnableGeofencing(checked)}
                    />

                    <label
                      className="form-check-label"
                      htmlFor="geofencingSwitch"
                    >
                      &nbsp; &nbsp;Enable Geofencing
                    </label>
                  </div>
                </div>

                {enableGeofencing && (
                  <>
                    <div className="mb-3">
                      <label htmlFor="radiusInput" className="form-label">
                        Geofence Radius (meters)
                      </label>

                      <input
                        type="number"
                        id="radiusInput"
                        className="form-control"
                        min="50"
                        max="5000"
                        value={radius}
                        onChange={(e) =>
                          setRadius(parseInt(e.target.value) || 100)
                        }
                      />

                      <div className="form-text">
                        Set the radius for the geofence area around the
                        location.
                      </div>
                    </div>

                    {loadError && (
                      <div className="alert alert-danger">
                        Error loading Google Maps. Please try again later.
                      </div>
                    )}

                    {!isLoaded ? (
                      <div>Loading Google Maps...</div>
                    ) : (
                      <div className="mb-3">
                        <GoogleMap
                          mapContainerStyle={mapContainerStyle}
                          center={mapCenter}
                          zoom={14}
                          options={{
                            streetViewControl: false,
                            mapTypeControl: false,
                            fullscreenControl: false,
                          }}
                        >
                          {markerPosition && (
                            <>
                              <Marker
                                position={markerPosition}
                                draggable={false}
                              />

                              <Circle
                                center={markerPosition}
                                radius={radius}
                                options={{
                                  strokeColor: "#FF0000",
                                  strokeOpacity: 0.8,
                                  strokeWeight: 2,
                                  fillColor: "#FF0000",
                                  fillOpacity: 0.35,
                                }}
                              />
                            </>
                          )}
                        </GoogleMap>

                        <div className="form-text">
                          Map showing the job location and geofence area.
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeAssignModal}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAssignElectrician}
                  disabled={isSubmitting || selectedElectricians.length === 0}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentLayer;